import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoute } from "wouter";

import { partTypeTire } from "../../../constants/partTypes";
import {
  productTypeTire,
  productTypeWheel,
} from "../../../constants/productTypes";
import { useAllowPartPricing } from "../../../hooks/useAllowPartPricing";
import { DealerVehicleResultsRoute } from "../../../navigation/DealerRoutes";
import { HashLink } from "../../../navigation/HashLink";
import { useAddItemToCart } from "../../../queries/useAddItemToCart";
import { useGetNewCart } from "../../../queries/useGetNewCart";
import { VehicleInfo } from "../../../queries/useGetVehicleInfo";
import { useRemoveItemFromCart } from "../../../queries/useRemoveItemFromCart";
import { StockItem } from "../../../queries/useSearchTiresByVehicle";
import { useDealerAccountInfo } from "../../../stores/hooks/useDealerAccountInfo";
import { useRelatedCartItems } from "../../../stores/hooks/useRelatedCartItems";
import { useSetInfoToast } from "../../../stores/hooks/useSetInfoToast";
import { AnyPartItem } from "../../../stores/slices/cartSlice";
import { Button, PrimaryButton } from "../../../ui/Button/Button";
import { PriceSelectEntry } from "../../PriceSelect/usePriceSelectState";
import { getProductModalHash } from "../../ProductDetails/ProductModal";
import { useTireSize } from "../../Tires/Results/Filters/TireSizeFilters/useTireSize";

type CartButtonProps = {
  className: string;
  createPackageClassName: null | string;
  isStaggered: boolean;
  part: Readonly<AnyPartItem> | null;
  pricing: PriceSelectEntry[];
  primary?: boolean;
  vehicleId: null | number;
  vehicleInfo: VehicleInfo | null;
};

export const CartButton: FunctionComponent<CartButtonProps> = ({
  className,
  createPackageClassName,
  isStaggered,
  part,
  pricing,
  primary,
  vehicleId,
  vehicleInfo,
}) => {
  const [tireSize] = useTireSize();
  const { t } = useTranslation();
  const addItemToCart = useAddItemToCart();
  const [location, setLocation] = useLocation();
  const removeItemFromCart = useRemoveItemFromCart();
  const { dealerId, dealerNationalAccountName } = useDealerAccountInfo();
  const relatedCartItems = useRelatedCartItems(part, isStaggered);
  const initCart = useGetNewCart();

  const [orderItem] = relatedCartItems;
  const [priceOption] = pricing;

  const [primaryPartPriceEntry] = pricing;

  const allowPricing = useAllowPartPricing(
    part,
    primaryPartPriceEntry?.pricingOption ?? null
  );
  const [isResultsRoute] = useRoute(DealerVehicleResultsRoute);
  const setInfoToast = useSetInfoToast();

  const addAllToCart = async (): Promise<void> => {
    const { orderNumber } = await initCart();
    for await (const item of pricing) {
      const { combinedPart, pricingOption } = item;
      const partStock: StockItem[] =
        combinedPart.itemType === partTypeTire &&
        combinedPart.itemId === part?.partDetails.primaryPart.itemId
          ? (part?.partStock.primaryPart as StockItem[])
          : [];

      await addItemToCart.send([
        {
          dealerId: Number(dealerId),
          itemId: combinedPart.itemId,
          itemType: combinedPart.itemType,
          orderNumber,
          partStock,
          pricingOption,
          vehicleId,
        },
      ]);
    }
  };

  const removeAllFromCart = async (): Promise<void> => {
    for await (const item of relatedCartItems) {
      if (item) {
        await removeItemFromCart.send(item!);
      }
    }
  };

  const redirectToWheels = (size?: null | string): void => {
    let nextLocation = location.replace(productTypeTire, productTypeWheel);
    if (size) {
      nextLocation = `${nextLocation}?size=${size}${window.location.hash}`;
    }
    setLocation(nextLocation);
    setInfoToast({
      message: t("cart.packageCreatedMessage"),
    });
  };

  if (!allowPricing) {
    return (
      <HashLink
        hash={getProductModalHash(part?.partDetails.primaryPart.itemId)}
      >
        <Button
          aria-label="openDealerContactForm"
          className={className}
          disabled={!part}
          element="a"
        >
          {t("cart.contactDealer")}
        </Button>
      </HashLink>
    );
  }

  const hasWheels = vehicleInfo?.vehicleFullTireSizeInfo.some(
    ({ hasWheels }) => !!Number(hasWheels)
  );

  const shouldDisplayCreatePackage =
    allowPricing &&
    hasWheels &&
    isResultsRoute &&
    part?.partDetails.primaryPart.itemType === partTypeTire;

  const CartButtonElement =
    primary || dealerNationalAccountName === "Mazda" ? PrimaryButton : Button;

  return (
    <>
      <CartButtonElement
        onClick={(): void => {
          if (part && priceOption) {
            orderItem ? removeAllFromCart() : addAllToCart();
          }
        }}
        aria-label={orderItem ? "removeFromQuote" : "addToQuote"}
        busy={addItemToCart.loading || removeItemFromCart.loading}
        className={clsx("tw-cart-button", className)}
        disabled={!part || addItemToCart.loading || removeItemFromCart.loading}
        type="button"
      >
        {t(orderItem ? "cart.removeFromQuote" : "cart.addToQuote")}
      </CartButtonElement>
      {shouldDisplayCreatePackage && (
        <Button
          disabled={
            !part || addItemToCart.loading || removeItemFromCart.loading
          }
          onClick={(): void => {
            if (!part || !priceOption) {
              return;
            }
            if (orderItem) {
              redirectToWheels(tireSize);
              return;
            }
            addAllToCart().then(() => {
              redirectToWheels(tireSize);
            });
          }}
          aria-label="createPackage"
          className={clsx("tw-create-package-button", createPackageClassName)}
          type="button"
        >
          {t("dealerTireTile.createPackage")}
        </Button>
      )}
    </>
  );
};
